import React, { useState, useEffect, useCallback, useMemo } from "react";
import { observer } from "mobx-react";
import { Modal, TextField, Typography, Select, Autocomplete } from "@ann/web-components";
import { useMediaQueries } from "../../../../../../../utils/mediaQueries";
import { useToast } from "../../../../../../../components/toast";
import { useStore } from "../../../../../../../hooks";
import { updateKnowledge } from "../../../../services/knowledge";
import {
  ModalBodyContent,
  FieldContainer,
  ErrorMessage,
} from "./EditKnowledgeModal.styled";

/**
 * No-operation function (noop) - a stable empty function reference.
 * Used as a placeholder for required callback props that don't need implementation.
 * Defined outside the component to maintain referential equality across renders,
 * avoiding unnecessary re-renders of child components.
 */
const noop = () => {};

const EditKnowledgeModal = observer(({
  isOpen,
  onClose,
  size = "medium",
  onSuccess,
  initialData,
  departmentsList = [],
}) => {
  const { isMobile } = useMediaQueries();
  const toast = useToast();
  const { usersStore } = useStore();

  const [knowledgeBaseName, setKnowledgeBaseName] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [ownerInputValue, setOwnerInputValue] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingOwner, setIsLoadingOwner] = useState(false);

  // Calculate modal width: 90% for mobile, 768px otherwise
  const modalWidth = useMemo(() => {
    if (isMobile) {
      return "90%";
    }
    return "768px";
  }, [isMobile]);

  // Transform departmentsList to format expected by Select component
  // API returns: departmentUuid, departmentName
  const departments = useMemo(() => {
    return departmentsList.map((dept) => ({
      department_uuid: dept.departmentUuid || dept.uuid,
      departmentUuid: dept.departmentUuid || dept.uuid,
      department_name: dept.departmentName || dept.name,
      departmentName: dept.departmentName || dept.name,
      name: dept.departmentName || dept.name,
    }));
  }, [departmentsList]);

  // Load users when modal opens
  useEffect(() => {
    if (!isOpen) return;
    
    // Only load if store is empty (users not loaded yet)
    if (usersStore.users.length === 0) {
      usersStore.getUserList({
        page: 1,
        page_size: 100, // Get all users
        order_by: "fullName",
        order_direction: "ASC",
      }).catch((error) => {
        console.error('Error loading users from UsersStore:', error);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, usersStore.users.length]);

  // Format users from store for Autocomplete
  const usersList = useMemo(() => {
    if (!usersStore.users || !Array.isArray(usersStore.users)) {
      return [];
    }
    return usersStore.users.map((user) => ({
      user_uuid: user.user_uuid || user.userUuid || user.uuid,
      full_name: user.fullName || user.full_name || "Unknown",
      email: user.email || "",
    }));
  }, [usersStore.users]);

  // Initialize form with initialData when modal opens
  useEffect(() => {
    if (isOpen && initialData) {
      // Check multiple possible field names for knowledge base name
      const kbName = initialData.name || initialData.kb_name || initialData.kbName || "";
      setKnowledgeBaseName(kbName);
    } else if (isOpen && !initialData) {
      // Reset form if no initial data
      setKnowledgeBaseName("");
      setSelectedDepartment(null);
      setSelectedOwner(null);
    }
  }, [isOpen, initialData]);

  // Set department when initialData has department_uuid
  useEffect(() => {
    if (!isOpen || !initialData || departments.length === 0) return;

    const deptUuid = initialData.department_uuid || initialData.departmentUuid;
    if (!deptUuid) {
      setSelectedDepartment(null);
      return;
    }

    // Check if department exists in list
    const existingDept = departments.find(
      (d) => (d.department_uuid || d.departmentUuid) === deptUuid
    );
    
    // Only set if exists, otherwise leave empty
    setSelectedDepartment(existingDept ? deptUuid : null);
  }, [isOpen, initialData, departments]);

  // Load owner when initialData has owner_uuid
  useEffect(() => {
    if (!isOpen || !initialData) return;

    const ownerUuid = initialData.owner_uuid || initialData.ownerUuid;
    if (!ownerUuid) {
      setSelectedOwner(null);
      setOwnerInputValue("");
      return;
    }

    // Check if owner is already in usersList
    const existingUser = usersList.find((u) => u.user_uuid === ownerUuid);
    if (existingUser) {
      setSelectedOwner(ownerUuid);
      setOwnerInputValue(existingUser.full_name);
      return;
    }

    // If not in list, fetch user by UUID
    setIsLoadingOwner(true);
    usersStore.getUserByUuid(ownerUuid)
      .then((user) => {
        if (user) {
          const userUuid = user.user_uuid || user.userUuid || user.uuid;
          const userName = user.fullName || user.full_name || "Unknown";
          
          // Add to usersList if not already there
          if (!usersList.find((u) => u.user_uuid === userUuid)) {
            usersStore.setUsers([...usersStore.users, user]);
          }
          
          setSelectedOwner(userUuid);
          setOwnerInputValue(userName);
        } else {
          // User not found, leave field empty
          setSelectedOwner(null);
          setOwnerInputValue("");
        }
      })
      .catch((error) => {
        console.error('Error loading owner from UsersStore:', error);
        // If user not found or error, leave field empty
        setSelectedOwner(null);
        setOwnerInputValue("");
      })
      .finally(() => {
        setIsLoadingOwner(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialData?.owner_uuid, initialData?.ownerUuid, usersList.length]);

  // Simple setters don't need useCallback - useState setters are stable
  const handleKnowledgeBaseNameChange = (value) => {
    setKnowledgeBaseName(value);
  };

  const handleDepartmentChange = (value) => {
    setSelectedDepartment(value);
  };

  const handleOwnerInputChange = useCallback((value) => {
    // onChange - user typing in the input
    setOwnerInputValue(value);
    // Reset selection when user types
    if (!value) {
      setSelectedOwner(null);
    }
  }, []);

  const handleOwnerSelect = useCallback((suggestion) => {
    // onSuggestionSelect - user selected a suggestion from the list
    if (suggestion) {
      const uuid = suggestion.user_uuid;
      const name = suggestion.full_name;
      setSelectedOwner(uuid);
      setOwnerInputValue(name);
    }
  }, []);

  const handleClose = useCallback(() => {
    // Reset form state
    setKnowledgeBaseName("");
    setSelectedDepartment(null);
    setSelectedOwner(null);
    setOwnerInputValue("");
    onClose();
  }, [onClose]);

  // Field validation states
  const isNameEmpty = !knowledgeBaseName.trim();
  const isDepartmentEmpty = !selectedDepartment;
  const isOwnerEmpty = !selectedOwner;

  const isFormValid = useMemo(() => {
    return !isNameEmpty && !isDepartmentEmpty && !isOwnerEmpty;
  }, [isNameEmpty, isDepartmentEmpty, isOwnerEmpty]);

  const handleSave = useCallback(async () => {
    if (!isFormValid) {
      return;
    }

    const knowledgebaseUuid = initialData?.knowledgebase_uuid || initialData?.knowledgebaseUuid || initialData?.uuid;
    if (!knowledgebaseUuid) {
      toast.error({
        message: "Knowledge base UUID not found",
        canClose: true,
      });
      return;
    }

    const trimmedName = knowledgeBaseName.trim();
    setIsSaving(true);

    try {
      await updateKnowledge({
        knowledgebase_uuid: knowledgebaseUuid,
        name: trimmedName,
        department_uuid: selectedDepartment,
        owner_uuid: selectedOwner || undefined,
      });

      // Success
      toast.success({
        message: `${trimmedName} was updated successfully`,
        canClose: true,
      });

      // Reset form and close modal
      setKnowledgeBaseName("");
      setSelectedDepartment(null);
      setSelectedOwner(null);
      onClose();

      // Refresh knowledge bases list
      onSuccess?.();
    } catch (error) {
      // Error handling
      const errorMessage =
        error.message || "An error occurred while updating knowledge base";
      toast.error({
        message: errorMessage,
        canClose: true,
      });
    } finally {
      setIsSaving(false);
    }
  }, [knowledgeBaseName, selectedDepartment, selectedOwner, isFormValid, initialData, toast, onClose, onSuccess]);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} width={modalWidth} size={size}>
      <Modal.Header>
        <Modal.Header.Title>Edit Knowledge Base</Modal.Header.Title>
      </Modal.Header>
      <Modal.Body>
        <ModalBodyContent>
          <FieldContainer>
            <Typography category="subtitle" size="medium" weight="bold">
              Knowledge Base Name
            </Typography>
            <TextField
              placeholder="Enter Knowledge base name"
              size="small"
              value={knowledgeBaseName}
              onChange={handleKnowledgeBaseNameChange}
            />
            {isNameEmpty && (
              <ErrorMessage>Knowledge base name is required</ErrorMessage>
            )}
          </FieldContainer>

          <FieldContainer>
            <Typography category="subtitle" size="medium" weight="bold">
              Departments
            </Typography>
            <Select
              onChange={handleDepartmentChange}
              onSelectionChange={handleDepartmentChange}
              size="small"
              width="100%"
              placeholder="Select a department"
              value={departments.find(d => (d.department_uuid || d.departmentUuid) === selectedDepartment)?.name}
            >
              <Select.Menu>
                {departments.map((dept) => {
                  const deptUuid = dept.department_uuid || dept.departmentUuid;
                  const deptName = dept.department_name || dept.departmentName || dept.name || "Unknown";
                  return (
                    <Select.Item
                      key={deptUuid}
                      label={deptName}
                      value={deptUuid}
                    >
                      {deptName}
                    </Select.Item>
                  );
                })}
              </Select.Menu>
            </Select>
            {isDepartmentEmpty && (
              <ErrorMessage>Department is required</ErrorMessage>
            )}
          </FieldContainer>

          <FieldContainer className="owner-field">
            <Typography category="subtitle" size="medium" weight="bold">
              Owner
            </Typography>
            <Autocomplete
              placeholder="Search and select owner"
              size="small"
              value={ownerInputValue}
              onChange={handleOwnerInputChange}
              onSuggestionSelect={handleOwnerSelect}
              onSearch={noop}
              suggestions={usersList}
              valueKey="user_uuid"
              labelKey="full_name"
              disabled={isLoadingOwner}
            />
            {isOwnerEmpty && (
              <ErrorMessage>Owner is required</ErrorMessage>
            )}
          </FieldContainer>
        </ModalBodyContent>
      </Modal.Body>
      <Modal.Actions align="end">
        <Modal.Button
          label="Cancel"
          onClick={handleClose}
          type="secondary"
          disabled={isSaving}
        />
        <Modal.Button
          label={isSaving ? "Saving..." : "Save Changes"}
          onClick={handleSave}
          disabled={!isFormValid || isSaving}
        />
      </Modal.Actions>
    </Modal>
  );
});

export default EditKnowledgeModal;
